// CalcWidget — small calculator for the POS header: 🖩 button toggles a
// floating keypad panel. Pure in-memory, supports keyboard input while open.
import React, { useState, useEffect, useCallback } from 'react';

const GREEN = '#2e9f86';

const fmt = (n) => {
  if (!Number.isFinite(n)) return 'Error';
  const s = String(Math.round(n * 1e8) / 1e8);
  return s.length > 12 ? n.toPrecision(8) : s;
};

export default function CalcWidget() {
  const [open, setOpen] = useState(false);
  const [disp, setDisp] = useState('0');     // current entry
  const [prev, setPrev] = useState(null);    // stored operand
  const [op, setOp] = useState(null);        // pending operator
  const [fresh, setFresh] = useState(true);  // next digit starts a new entry

  const calc = (a, o, b) => {
    switch (o) {
      case '+': return a + b;
      case '-': return a - b;
      case '×': return a * b;
      case '÷': return b === 0 ? NaN : a / b;
      default: return b;
    }
  };

  const digit = useCallback((d) => {
    setDisp((cur) => {
      if (fresh || cur === '0' || cur === 'Error') return d === '.' ? '0.' : d;
      if (d === '.' && cur.includes('.')) return cur;
      return cur.length >= 14 ? cur : cur + d;
    });
    setFresh(false);
  }, [fresh]);

  const operator = useCallback((o) => {
    setDisp((cur) => {
      const v = parseFloat(cur) || 0;
      if (prev != null && op && !fresh) {
        const r = calc(prev, op, v);
        setPrev(r); setOp(o); setFresh(true);
        return fmt(r);
      }
      setPrev(v); setOp(o); setFresh(true);
      return cur;
    });
  }, [prev, op, fresh]);

  const equals = useCallback(() => {
    setDisp((cur) => {
      if (prev == null || !op) return cur;
      const r = calc(prev, op, parseFloat(cur) || 0);
      setPrev(null); setOp(null); setFresh(true);
      return fmt(r);
    });
  }, [prev, op]);

  const clear = () => { setDisp('0'); setPrev(null); setOp(null); setFresh(true); };
  const back = () => { setDisp((c) => (fresh || c.length <= 1 ? '0' : c.slice(0, -1))); };
  const percent = () => { setDisp((c) => fmt((parseFloat(c) || 0) / 100)); setFresh(true); };

  // keyboard support while panel is open
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      // don't steal typing from inputs (barcode/search fields)
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (/^[0-9.]$/.test(e.key)) { digit(e.key); e.preventDefault(); }
      else if (e.key === '+') operator('+');
      else if (e.key === '-') operator('-');
      else if (e.key === '*') operator('×');
      else if (e.key === '/') { operator('÷'); e.preventDefault(); }
      else if (e.key === 'Enter' || e.key === '=') { equals(); e.preventDefault(); }
      else if (e.key === 'Backspace') back();
      else if (e.key === 'Escape') setOpen(false);
      else if (e.key.toLowerCase() === 'c') clear();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, digit, operator, equals]);

  const Btn = ({ children, onClick, wide, accent, dark }) => (
    <button onClick={onClick}
      className={`${wide ? 'col-span-2' : ''} rounded-lg py-2.5 text-base font-semibold active:scale-95 transition-transform select-none`}
      style={{
        background: accent ? GREEN : dark ? '#374151' : '#f3f4f6',
        color: accent || dark ? '#fff' : '#1f2937',
      }}>
      {children}
    </button>
  );

  return (
    <>
      <button onClick={() => setOpen((o) => !o)} title="Calculator"
        className="shrink-0 rounded-full px-3 py-1 text-xs font-semibold flex items-center gap-1 shadow-sm"
        style={{
          background: open ? '#374151' : '#fff',
          color: open ? '#fff' : '#374151',
          border: open ? 'none' : '1px solid #d1d5db',
        }}>
        🖩
      </button>

      {open && (
        <div className="fixed z-[996] bg-white rounded-2xl shadow-2xl border border-gray-200 p-3 w-[240px]"
          style={{ top: 96, insetInlineEnd: 24 }} dir="ltr">
          <div className="bg-gray-900 text-white rounded-lg px-3 py-2 mb-2 text-right">
            <div className="text-[10px] text-gray-400 h-3.5">
              {prev != null && op ? `${fmt(prev)} ${op}` : ''}
            </div>
            <div className="text-2xl font-bold tabular-nums overflow-x-auto">{disp}</div>
          </div>
          <div className="grid grid-cols-4 gap-1.5">
            <Btn dark onClick={clear}>C</Btn>
            <Btn dark onClick={back}>⌫</Btn>
            <Btn dark onClick={percent}>%</Btn>
            <Btn accent onClick={() => operator('÷')}>÷</Btn>
            <Btn onClick={() => digit('7')}>7</Btn>
            <Btn onClick={() => digit('8')}>8</Btn>
            <Btn onClick={() => digit('9')}>9</Btn>
            <Btn accent onClick={() => operator('×')}>×</Btn>
            <Btn onClick={() => digit('4')}>4</Btn>
            <Btn onClick={() => digit('5')}>5</Btn>
            <Btn onClick={() => digit('6')}>6</Btn>
            <Btn accent onClick={() => operator('-')}>−</Btn>
            <Btn onClick={() => digit('1')}>1</Btn>
            <Btn onClick={() => digit('2')}>2</Btn>
            <Btn onClick={() => digit('3')}>3</Btn>
            <Btn accent onClick={() => operator('+')}>+</Btn>
            <Btn wide onClick={() => digit('0')}>0</Btn>
            <Btn onClick={() => digit('.')}>.</Btn>
            <Btn accent onClick={equals}>=</Btn>
          </div>
        </div>
      )}
    </>
  );
}
